import { useState, useEffect, useMemo, useRef } from "react";
import { API_URL } from "@/config/api";
import { SITE_URL } from "@/config/site";
import { MODEL_COUNT, PROVIDER_COUNT } from "@/data/modelStats";
import {
  PLAN_PRICES,
  BILLING_DISCOUNTS,
  PLAN_TIERS,
  TRIAL_DAYS,
} from "@/data/plans";
import { REPLACED_TOOLS } from "@/data/competitors";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { trackCheckoutStart } from "@/lib/analytics";
import { isNative } from "@/utils/capacitor";
import {
  isRevenueCatAvailable,
  initRevenueCat,
  getPackages,
  purchasePackage,
  restorePurchases,
} from "@/lib/revenuecat";
import {
  Bot,
  Check,
  ArrowLeft,
  Sparkles,
  Loader2,
  Lightbulb,
} from "lucide-react";
import { useModelCount } from "@/hooks/useModelCount";

// Labels are resolved at render via t(`pricing.billing.${key}`) — see the
// toggle below. Only the invariant key + discount badge live here.
const BILLING_PERIODS = [
  { key: "monthly" },
  {
    key: "quarterly",
    badge: `-${Math.round(BILLING_DISCOUNTS.quarterly * 100)}%`,
  },
  { key: "yearly", badge: `-${Math.round(BILLING_DISCOUNTS.yearly * 100)}%` },
];

// Static plan catalog — the render fallback + prerender/SEO source so the
// conversion-critical pricing grid is NEVER blank. Previously the grid was
// fed only by a client fetch of /api/subscriptions/plans; on a Render
// cold-start (30-50s), a backend outage, or any fetch failure the grid
// rendered empty (plans stayed []), AND the prerendered HTML shipped with no
// prices at all (bad for SEO on a page we prerender for 15 locales). Prices
// are the §7 constants; period totals apply the BILLING_PERIODS discounts
// (-10% quarterly, -30% yearly). Names/features come from the localized
// plans.* i18n namespace (all 15 locales) -> no translation debt, fully
// localized in every language, and never blank / cold-start-gated. The real
// charge is still authored server-side at the LemonSqueezy checkout
// (handleSelectPlan); only the *display* catalog is static.
// Derived from the single price source (data/plans.js) — never hardcode a plan
// price here again; edit PLAN_PRICES and both this grid and the landing JSON-LD
// follow.
const STATIC_PLAN_BASE = PLAN_TIERS.map(({ id, builds_on }) => ({
  id,
  base: PLAN_PRICES[id],
  builds_on,
}));

const roundPrice = (n) => Math.round(n * 100) / 100;

const getStaticPlans = (t) =>
  STATIC_PLAN_BASE.map(({ id, base, builds_on }) => {
    const features = t(`plans.${id}.features`, { returnObjects: true });
    return {
      id,
      name: t(`plans.${id}.name`),
      price: base,
      prices: {
        monthly: base,
        quarterly: roundPrice(base * 3 * (1 - BILLING_DISCOUNTS.quarterly)),
        yearly: roundPrice(base * 12 * (1 - BILLING_DISCOUNTS.yearly)),
      },
      features: Array.isArray(features) ? features : [],
      ...(builds_on ? { builds_on } : {}),
    };
  });

const PricingPage = () => {
  const { t, i18n } = useTranslation();
  const pricingJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Puis-je changer de plan à tout moment ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui ! Vous pouvez upgrader ou downgrader votre plan à tout moment. Les changements prennent effet immédiatement.",
            },
          },
          {
            "@type": "Question",
            name: "Y a-t-il une période d'essai ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: `Oui : les abonnements mensuel et trimestriel incluent ${TRIAL_DAYS.default} jours d'essai gratuit, et l'abonnement annuel ${TRIAL_DAYS.yearly} jours. Aucun paiement avant la fin de l'essai, résiliable à tout moment.`,
            },
          },
          {
            "@type": "Question",
            name: "Comment fonctionnent les réductions ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Le paiement trimestriel offre 10% de réduction, et le paiement annuel offre 30% de réduction par rapport au prix mensuel.",
            },
          },
          {
            "@type": "Question",
            name: "Puis-je annuler mon abonnement ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui, vous pouvez annuler à tout moment. L'accès reste actif jusqu'à la fin de la période payée.",
            },
          },
          {
            "@type": "Question",
            name: "Mes données sont-elles sécurisées ?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Absolument. Toutes les communications sont chiffrées et vos documents ne sont jamais partagés.",
            },
          },
        ],
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: SITE_URL,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Tarifs",
            item: `${SITE_URL}/pricing`,
          },
        ],
      },
    ],
  };

  const navigate = useNavigate();
  const { isAuthenticated, token, user, refreshUser } = useAuth();
  const [restoring, setRestoring] = useState(false);

  // Native In-App Purchase (Apple 3.1.1): configure RevenueCat with our user id
  // so its webhook maps back to db.users.id. No-op on web / when unconfigured.
  useEffect(() => {
    if (isRevenueCatAvailable() && user?.id) initRevenueCat(user.id);
  }, [user?.id]);
  // Plans render from the localized static catalog (getStaticPlans). The old
  // public /api/subscriptions/plans fetch was removed: it is not
  // user-contextual, its prices are the §7 constants (also computed here via
  // BILLING_PERIODS), the real charge is authored server-side at the
  // LemonSqueezy checkout (handleSelectPlan) — and it returned FR/EN-mixed
  // feature strings that read as unprofessional on a FR-first conversion page.
  // i18n is now the single source for names/features across all 15 locales,
  // so the grid is always fully localized and never blank / cold-start-gated.
  // Must be reactive, not a useState initializer: on direct navigation to a
  // non-default locale (/ar/pricing, /zh/pricing…) the translation JSON loads
  // async, so a one-shot initializer freezes the grid to the English fallback
  // forever. Recompute when t / the active language changes (react-i18next
  // bumps `t` identity on the 'languageChanged'+'loaded' events).
  const plans = useMemo(() => getStaticPlans(t), [t, i18n.language]);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [searchParams] = useSearchParams();

  // Replace-5-tools widget — interactive savings calculator. Each rival tool can
  // be toggled off so the visitor sees THEIR own stack; the stacked total and the
  // savings vs Stronger Bot Pro are derived (never hardcoded) from the single
  // sources data/competitors.js (REPLACED_TOOLS) + data/plans.js (PLAN_PRICES).
  const [activeTools, setActiveTools] = useState(() =>
    REPLACED_TOOLS.map(() => true)
  );
  const toggleTool = (i) =>
    setActiveTools((prev) => prev.map((on, idx) => (idx === i ? !on : on)));
  const rivalsTotal = REPLACED_TOOLS.reduce(
    (sum, tool, i) => (activeTools[i] ? sum + tool.priceUsd : sum),
    0
  );
  // Stronger Bot Pro monthly price (EUR, single source). Savings keeps the
  // widget's existing figure semantics (stacked $ total − Pro price).
  const proPrice = Math.round(PLAN_PRICES.pro);
  const savingsAmount = Math.max(0, rivalsTotal - proPrice);

  // Live model + provider counts (Replace-5-tools widget). Fallback is
  // the known prod state at PR time (2026-05-21: 29 models · 10
  // providers, same as the dashboard badge) so SEO crawlers and a
  // momentary network blip never see "0 modèles".
  const modelCount = useModelCount({
    models: MODEL_COUNT,
    providers: PROVIDER_COUNT,
  });

  // Phase — beta-tester promo code visibility (user request 2026-05-25,
  // upgraded 2026-05-29 to both-codes model).
  // CLAUDE.md §7: LAUNCH30 (-30% trimestriel) + LAUNCH50 (-50% annuel)
  // are reserved to beta testers — never advertised publicly. As of the
  // 2026-05-29 change every beta tester gets BOTH codes; the chip shows
  // on whichever billing period matches each code (no more redirect hint).
  const [betaStatus, setBetaStatus] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API_URL}/api/beta/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setBetaStatus(r.data || null))
      .catch(() => {
        // Silent — beta hint is opportunistic, not load-bearing.
      });
  }, [token]);

  // Prefer the new `codes` array; fall back to legacy single `code` for
  // a brief transition window where an old FE bundle could be paired
  // with the new backend (or vice versa).
  const betaCodes = betaStatus?.is_beta
    ? betaStatus.codes && betaStatus.codes.length > 0
      ? betaStatus.codes
      : betaStatus.code
      ? [betaStatus.code]
      : []
    : [];
  const hasLaunch30 = betaCodes.includes("LAUNCH30");
  const hasLaunch50 = betaCodes.includes("LAUNCH50");
  const activeBetaCode =
    billingPeriod === "quarterly" && hasLaunch30
      ? "LAUNCH30"
      : billingPeriod === "yearly" && hasLaunch50
      ? "LAUNCH50"
      : null;

  const autoselectFiredRef = useRef(false);
  useEffect(() => {
    const autoselect = searchParams.get("autoselect");
    const autobilling = searchParams.get("billing");
    if (autobilling) setBillingPeriod(autobilling);
    if (
      autoselect &&
      isAuthenticated &&
      plans.length > 0 &&
      !isNative &&
      !autoselectFiredRef.current
    ) {
      // Skip auto-checkout on native (paid purchase happens on the web, per store
      // billing policy) — otherwise the register→pricing redirect would fire a
      // checkout the native app must not initiate.
      // The ref guards against the effect re-running when `plans` is recomputed
      // on an async locale load — the checkout must fire at most once.
      const plan = plans.find((p) => p.id === autoselect);
      if (plan) {
        autoselectFiredRef.current = true;
        setTimeout(() => handleSelectPlan(autoselect), 300);
      }
    }
  }, [searchParams, isAuthenticated, plans]);

  const getDisplayPrice = (plan) => {
    if (plan.id === "free") return 0;
    const prices = plan.prices || {};
    return prices[billingPeriod] || plan.price || 0;
  };

  const getMonthlyEquivalent = (plan) => {
    if (plan.id === "free") return 0;
    const price = getDisplayPrice(plan);
    if (billingPeriod === "quarterly") return (price / 3).toFixed(2);
    if (billingPeriod === "yearly") return (price / 12).toFixed(2);
    return price;
  };

  const getPeriodLabel = () => {
    if (billingPeriod === "quarterly")
      return t("pricing.perQuarter", "/trimestre");
    if (billingPeriod === "yearly") return t("pricing.perYear", "/an");
    return t("pricing.perMonth", "/mois");
  };

  // Match a RevenueCat package to the selected tier + billing period by the
  // tokens embedded in its store product id (com.strongbot.<tier>.<period>).
  const _periodTokens = {
    monthly: ["month"],
    quarterly: ["quarter"],
    yearly: ["annual", "year"],
  };
  const findPackage = (packages, planId) => {
    const periods = _periodTokens[billingPeriod] || ["month"];
    return (
      packages.find((p) => {
        const pid = (p?.product?.identifier || "").toLowerCase();
        return pid.includes(planId) && periods.some((tok) => pid.includes(tok));
      }) || null
    );
  };

  const handleNativePurchase = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const packages = await getPackages();
      const pkg = findPackage(packages, planId);
      if (!pkg) {
        toast.error(
          t(
            "pricing.nativeNoProduct",
            "Ce plan n'est pas disponible à l'achat pour le moment."
          )
        );
        return;
      }
      const { active } = await purchasePackage(pkg);
      if (active) {
        toast.success(
          t("pricing.nativePurchaseOk", "Abonnement activé, merci !")
        );
        // The RevenueCat webhook sets the plan server-side; refresh to reflect
        // it (best-effort — the entitlement is already active on-device).
        setTimeout(() => refreshUser && refreshUser(), 1500);
      }
    } catch (e) {
      // RevenueCat throws a userCancelled error when the sheet is dismissed —
      // that's not a failure, so stay quiet on cancel.
      if (!e?.userCancelled && e?.code !== "1") {
        toast.error(
          t("pricing.nativePurchaseErr", "L'achat n'a pas pu aboutir.")
        );
      }
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const { active } = await restorePurchases();
      toast[active ? "success" : "info"](
        active
          ? t("pricing.restoreOk", "Achats restaurés.")
          : t("pricing.restoreNone", "Aucun achat à restaurer.")
      );
      if (active) setTimeout(() => refreshUser && refreshUser(), 1500);
    } catch {
      toast.error(t("pricing.restoreErr", "La restauration a échoué."));
    } finally {
      setRestoring(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    // Native store compliance (Apple guideline 3.1.1 / Google Play Billing): the
    // app must not sell a digital subscription through an in-app web checkout.
    // On native, paid plans are managed on the web and the app only reflects the
    // already-active plan (kept in sync via user.subscription_plan). Free stays
    // usable (no payment involved).
    if (isNative && planId !== "free") {
      // Native In-App Purchase via RevenueCat (Apple 3.1.1). When configured,
      // buy the matching package through StoreKit / Play Billing. Until the
      // §2 keys/offering are set, fall back to the web-management message so
      // the app never dead-ends.
      if (isRevenueCatAvailable()) {
        await handleNativePurchase(planId);
      } else {
        toast.info(
          t(
            "pricing.nativeManageOnWeb",
            "Les abonnements se gèrent sur strongerbot.com depuis un navigateur web."
          )
        );
      }
      return;
    }

    if (!isAuthenticated) {
      navigate(`/register?plan=${planId}&billing=${billingPeriod}`);
      return;
    }

    if (planId === "free") {
      toast.info(
        t("pricing.toast.alreadyFree", "Vous êtes déjà sur le plan gratuit")
      );
      return;
    }

    if (user?.subscription_plan === planId) {
      toast.info(
        t("pricing.toast.alreadyOn", "Vous êtes déjà abonné à ce plan")
      );
      return;
    }

    setCheckoutLoading(planId);

    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/checkout`,
        {
          plan_id: planId,
          billing_period: billingPeriod,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.checkout_url) {
        // GA4 `begin_checkout` key event — the paid-intent ad-conversion signal
        // fired as the user leaves for the LemonSqueezy checkout (ADS_ASSETS.md §5).
        trackCheckoutStart(planId, billingPeriod);
        // Redirect to LemonSqueezy checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.success(
          response.data.message || t("pricing.toast.activated", "Plan activé !")
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          t("pricing.toast.checkoutError", "Erreur lors du checkout")
      );
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPlanColor = (index, totalPlans) => {
    // Highlight Pro plan (index depends on how plans are sorted)
    const colors = [
      "from-slate-500",
      "from-blue-500",
      "from-primary",
      "from-violet-500",
      "from-amber-500",
    ];
    return colors[index] || "from-slate-500";
  };

  // Find which plan index is "popular" (Pro)
  const getPopularIndex = () => {
    return plans.findIndex((p) => p.id === "pro");
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Tarifs - Plans Gratuit, Pro et Business"
        description="Découvrez les plans tarifaires Stronger Bot. Commencez gratuitement, passez Pro pour des fonctionnalités avancées."
        canonical="/pricing"
        jsonLd={pricingJsonLd}
      />
      {/* Header */}
      <nav className="glass sticky top-0 z-50 border-b border-white/10 safe-area-top">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-white text-lg">
              Stronger Bot
            </span>
          </Link>
          <Link to={isAuthenticated ? "/dashboard" : "/"}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("pricing.back", "Retour")}
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-8 px-4 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">
              {t("pricing.badge", "Choisissez votre plan")}
            </span>
          </div>
          <h1 className="animate-in-up font-heading text-fluid-h1 font-bold text-white mb-4">
            {t("pricing.title", "Des prix adaptés à")}{" "}
            <span className="text-gradient">
              {t("pricing.titleHighlight", "vos besoins")}
            </span>
          </h1>
          <p
            className="animate-in-up text-fluid-lead text-slate-400 max-w-2xl mx-auto"
            style={{ "--d": "100ms" }}
          >
            {t(
              "pricing.subtitle",
              "Commencez gratuitement et évoluez selon vos besoins. Pas d'engagement, annulez à tout moment."
            )}
          </p>
        </div>
      </section>

      {/* Replace-5-tools widget (Phase 8.4 P0 — conversion narrative) */}
      <section className="pb-10 px-4" data-testid="replace-5-tools">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-3xl p-6 md:p-8">
            <div className="text-center mb-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                {t("pricing.replace.title", "Tu remplaces")}{" "}
                <span className="text-gradient">
                  {t("pricing.replace.titleHighlight", "5 outils par 1")}
                </span>
              </h2>
              <p className="text-slate-400 text-sm">
                {t(
                  "pricing.replace.subtitle",
                  "Pourquoi payer 5 abonnements quand un seul suffit ?"
                )}
              </p>
            </div>

            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
              {/* Left: the stack of competitor tools — interactive. Toggle a tool
                  off to model your own stack; the total + savings recompute live
                  from data/competitors.js (no hardcoded sum). */}
              <ul className="space-y-2 text-sm">
                {REPLACED_TOOLS.map((tool, i) => {
                  const on = activeTools[i];
                  return (
                    <li key={tool.name}>
                      <button
                        type="button"
                        onClick={() => toggleTool(i)}
                        aria-pressed={on}
                        data-testid={`savings-tool-${i}`}
                        className={`w-full flex items-center justify-between rounded-lg px-3 py-2 border transition-colors ${
                          on
                            ? "bg-white/5 border-white/10"
                            : "bg-transparent border-white/5 opacity-45"
                        }`}
                      >
                        <span className="flex items-center gap-2 text-slate-300">
                          <span
                            className={`w-4 h-4 rounded flex items-center justify-center border ${
                              on
                                ? "bg-primary border-primary text-white"
                                : "border-white/20"
                            }`}
                            aria-hidden="true"
                          >
                            {on && <Check className="w-3 h-3" />}
                          </span>
                          {tool.name}
                        </span>
                        <span className="text-slate-400 font-mono text-xs">
                          {tool.priceUsd} ${t("pricing.perMonth", "/mois")}
                        </span>
                      </button>
                    </li>
                  );
                })}
                <li className="flex items-center justify-between pt-2 border-t border-white/10 mt-3">
                  <span className="text-slate-200 font-semibold">
                    {t("pricing.replace.total", "Total")}
                  </span>
                  <span
                    className="text-red-400 font-mono font-bold"
                    data-testid="savings-total"
                  >
                    {rivalsTotal} ${t("pricing.perMonth", "/mois")}
                  </span>
                </li>
              </ul>

              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center text-primary text-4xl font-bold">
                →
              </div>
              <div className="flex md:hidden items-center justify-center text-primary text-3xl font-bold">
                ↓
              </div>

              {/* Right: Stronger Bot Pro */}
              <div className="bg-primary/10 border-2 border-primary/40 rounded-2xl p-5 text-center">
                <div className="inline-flex items-center gap-1 bg-primary/20 text-foreground px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide mb-3">
                  <Sparkles className="w-3 h-3" />
                  {t("pricing.replace.oneSub", "Une seule sub")}
                </div>
                <div className="text-white font-heading text-2xl font-bold mb-1">
                  Stronger Bot Pro
                </div>
                <div className="text-gradient font-heading text-4xl font-bold mb-2">
                  {proPrice} €
                  <span className="text-base text-slate-400">
                    {t("pricing.perMonth", "/mois")}{" "}
                    <span className="text-slate-500 text-sm">
                      {t("pricing.taxExcl", "HT")}
                    </span>
                  </span>
                </div>
                <div
                  className="text-green-400 text-sm font-semibold mb-3"
                  data-testid="savings-amount"
                >
                  {t("pricing.replace.saves", "Économise {{amount}} $/mois", {
                    amount: savingsAmount,
                  })}
                </div>
                <p
                  className="text-slate-400 text-xs leading-relaxed"
                  data-testid="replace-5-tools-features"
                >
                  {t(
                    "pricing.replace.features",
                    "{{models}} modèles IA · {{providers}} providers · génération PPTX/DOCX/XLSX · images FLUX · voix Whisper · agents · Deep Research",
                    {
                      models: modelCount.models,
                      providers: modelCount.providers,
                    }
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Billing Period Toggle */}
      <section className="pb-8 px-4">
        <div className="max-w-md mx-auto">
          <div className="glass rounded-2xl p-1.5 flex gap-1">
            {BILLING_PERIODS.map((period) => (
              <button
                key={period.key}
                onClick={() => setBillingPeriod(period.key)}
                aria-pressed={billingPeriod === period.key}
                data-testid={`billing-${period.key}`}
                className={`
                  flex-1 relative min-h-[44px] py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60
                  ${
                    billingPeriod === period.key
                      ? "bg-primary-strong text-white shadow-lg shadow-primary/25"
                      : "text-slate-400 hover:text-white"
                  }
                `}
              >
                {t(
                  `pricing.billing.${period.key}`,
                  {
                    monthly: "Mensuel",
                    quarterly: "Trimestriel",
                    yearly: "Annuel",
                  }[period.key]
                )}
                {period.badge && (
                  <span
                    className={`
                      absolute -top-2 -right-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full
                      ${
                        billingPeriod === period.key
                          ? "bg-green-500 text-white"
                          : "bg-green-500/20 text-green-400"
                      }
                    `}
                  >
                    {period.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Annual savings nudge — only visible when monthly is selected
              (Phase 8.4 P1). Pushes ARR by reminding users yearly is -30%
              ≈ 3.6 mois offerts. */}
          {billingPeriod === "monthly" && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setBillingPeriod("yearly")}
                className="inline-flex items-center gap-1.5 text-xs text-green-400 hover:text-green-300 underline-offset-2 hover:underline focus:outline-none focus-visible:underline transition-colors"
                data-testid="annual-savings-nudge"
              >
                <Lightbulb className="w-3.5 h-3.5" />
                {t(
                  "pricing.annualNudge",
                  "Passe en annuel pour économiser 30 % (≈ 3,6 mois offerts)"
                )}
              </button>
            </div>
          )}

          {/* Beta-tester promo code chip. 2026-05-29: every beta tester
              now gets BOTH codes (LAUNCH30 trimestriel, LAUNCH50 annuel)
              — the chip simply mirrors whichever code matches the active
              billing period. No more redirect hint: the previous
              one-code-per-user model forced testers to switch period to
              honor the code they had, which was awkward when both codes
              were already valid at LemonSqueezy. Non-beta users see
              nothing per CLAUDE.md §7 (codes stay off public banners). */}
          {activeBetaCode && (
            <div
              className="mt-3 flex items-center justify-center"
              data-testid="beta-launch-code-hint"
            >
              <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-1.5 text-xs">
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                <span className="text-slate-300">
                  {t("pricing.beta.label", "Beta tester · ajoute le code")}{" "}
                </span>
                <code
                  className="font-mono font-bold text-accent"
                  data-testid="beta-launch-code-value"
                >
                  {activeBetaCode}
                </code>
                <span className="text-slate-400">
                  {" "}
                  {activeBetaCode === "LAUNCH30"
                    ? t(
                        "pricing.beta.launch30",
                        "au checkout pour −30 % en plus du −10 % trimestriel"
                      )
                    : t(
                        "pricing.beta.launch50",
                        "au checkout pour −50 % en plus du −30 % annuel"
                      )}
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Launch-period promo banner removed on 2026-04-21: LAUNCH30 and
          LAUNCH50 are now reserved for lifetime beta testers only. The codes
          remain active in LemonSqueezy so beta testers can still apply them
          at checkout; they are no longer advertised publicly. */}
      {/* Pricing Cards */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {(Array.isArray(plans) ? plans : []).map((plan, index) => {
              const isPopular = plan.id === "pro";
              const displayPrice = getDisplayPrice(plan);
              const monthlyEquiv = getMonthlyEquivalent(plan);

              return (
                <div
                  key={plan.id}
                  className={`
                      glass rounded-3xl p-6 relative overflow-hidden transition-transform duration-200
                      ${
                        isPopular
                          ? "border-primary md:scale-105 md:-my-2 ring-2 ring-primary/50"
                          : "lift"
                      }
                    `}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {/* Gradient background for popular plan */}
                  {isPopular && (
                    <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent" />
                  )}

                  {/* Popular badge */}
                  {isPopular && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-primary-strong text-white text-xs font-medium px-3 py-1 rounded-full">
                        {t("pricing.popular", "Populaire")}
                      </span>
                    </div>
                  )}

                  <div className="relative z-10">
                    {/* Plan name */}
                    <h3 className="font-heading text-xl font-bold text-white mb-1">
                      {plan.name}
                    </h3>

                    {/* Price */}
                    <div className="mb-1">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-white">
                          {displayPrice === 0
                            ? t("pricing.freePrice", "Gratuit")
                            : `€${displayPrice}`}
                        </span>
                        {displayPrice > 0 && (
                          <span className="text-slate-400 text-sm">
                            {getPeriodLabel()}{" "}
                            <span className="text-slate-500">
                              {t("pricing.taxExcl", "HT")}
                            </span>
                          </span>
                        )}
                      </div>
                      {/* Show monthly equivalent for quarterly/yearly */}
                      {billingPeriod !== "monthly" && displayPrice > 0 && (
                        <p className="text-xs text-green-400 mt-1">
                          {t("pricing.monthlyEquiv", "soit €{{price}}/mois", {
                            price: monthlyEquiv,
                          })}
                        </p>
                      )}
                    </div>

                    {/* Free trial badge — 14 days on the yearly plan (a
                        deliberate nudge to annual), 7 days on monthly/quarterly.
                        Mirrors the LemonSqueezy variant trial config. */}
                    {displayPrice > 0 && (
                      <div className="mb-4">
                        {/* Two grammatically-correct per-language keys rather
                            than one {{days}} template: languages like Arabic
                            inflect the noun differently for 7 vs 14 (أيام vs
                            يومًا), so a single interpolated template would be
                            ungrammatical. TRIAL_DAYS stays the source for the
                            numbers in the JSON-LD + FAQ prose. */}
                        <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                          {billingPeriod === "yearly"
                            ? t("pricing.trial14", "14 jours d'essai gratuit")
                            : t("pricing.trial", "7 jours d'essai gratuit")}
                        </span>
                      </div>
                    )}

                    {displayPrice === 0 && <div className="mb-4 h-6" />}

                    {/* Builds-on label (shows inheritance from previous tier) */}
                    {plan.builds_on &&
                      (() => {
                        const parent = plans.find(
                          (p) => p.id === plan.builds_on
                        );
                        const parentName = parent?.name || plan.builds_on;
                        return (
                          <p
                            data-testid={`plan-builds-on-${plan.id}`}
                            className="text-slate-400 text-xs italic mb-2"
                          >
                            {t(
                              "plans.builds_on_label",
                              "Tout du plan {{plan}}, plus :",
                              { plan: parentName }
                            )}
                          </p>
                        );
                      })()}

                    {/* Features */}
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-slate-300 text-sm"
                        >
                          <div
                            className={`
                                w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5
                                ${isPopular ? "bg-primary/20" : "bg-white/10"}
                              `}
                          >
                            <Check
                              className={`w-2.5 h-2.5 ${
                                isPopular ? "text-primary" : "text-white"
                              }`}
                            />
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Button
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={
                        checkoutLoading === plan.id ||
                        user?.subscription_plan === plan.id ||
                        (isNative && plan.id !== "free")
                      }
                      className={`
                          w-full py-5 text-sm font-medium
                          ${
                            isPopular
                              ? "btn-glow bg-primary-strong hover:bg-primary-strong/90 text-white"
                              : "bg-primary/10 text-primary border border-primary/25 hover:bg-primary/20"
                          }
                        `}
                      data-testid={`select-plan-${plan.id}`}
                    >
                      {checkoutLoading === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : user?.subscription_plan === plan.id ? (
                        t("pricing.currentPlan", "Plan actuel")
                      ) : plan.id === "free" ? (
                        t("pricing.startFree", "Commencer gratuitement")
                      ) : isNative ? (
                        t("pricing.nativeOnWeb", "Disponible sur le web")
                      ) : (
                        t("pricing.choosePlan", "Choisir ce plan")
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <p
            data-testid="pricing-vat-note"
            className="mt-6 text-center text-xs text-slate-400 max-w-lg mx-auto"
          >
            {t(
              "pricing.vatNote",
              "Prix affichés hors taxes (HT). La TVA applicable dans votre pays est calculée et ajoutée au moment du paiement."
            )}
          </p>
          {isNative && isRevenueCatAvailable() && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <Button
                variant="ghost"
                onClick={handleRestore}
                disabled={restoring}
                data-testid="pricing-restore"
                className="text-sm text-slate-300 hover:text-primary"
              >
                {restoring
                  ? t("pricing.restoring", "Restauration…")
                  : t("pricing.restore", "Restaurer mes achats")}
              </Button>
              <p className="text-center text-xs text-slate-400 max-w-md mx-auto">
                {t(
                  "pricing.nativeIapNote",
                  "Abonnement auto-renouvelable facturé via votre compte App Store. Gérez ou résiliez à tout moment dans les réglages de l'App Store."
                )}
              </p>
            </div>
          )}
          {isNative && !isRevenueCatAvailable() && (
            <p className="mt-3 text-center text-xs text-slate-400 max-w-md mx-auto">
              {t(
                "pricing.nativeManageOnWeb",
                "Les abonnements se gèrent sur strongerbot.com depuis un navigateur web."
              )}
            </p>
          )}
        </div>
      </section>

      {/* Compare with other AI assistants — discreet link */}
      <section className="py-6 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <Link
            to="/compare"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-primary rounded-md px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            data-testid="pricing-link-compare"
          >
            {t("pricing.compareLink", "Comparer avec d'autres assistants IA")}
            <span aria-hidden>→</span>
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-y-sm px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-fluid-h2 font-bold text-white text-center mb-12">
            {t("pricing.faq.title", "Questions fréquentes")}
          </h2>

          <div className="space-y-4">
            {[
              {
                q: t(
                  "pricing.faq.q1",
                  "Puis-je changer de plan à tout moment ?"
                ),
                a: t(
                  "pricing.faq.a1",
                  "Oui ! Vous pouvez upgrader ou downgrader votre plan à tout moment. Les changements prennent effet immédiatement."
                ),
              },
              {
                q: t("pricing.faq.q2", "Y a-t-il une période d'essai ?"),
                a: t(
                  "pricing.faq.a2",
                  "Oui : les abonnements mensuel et trimestriel incluent {{days}} jours d'essai gratuit, et l'abonnement annuel {{yearlyDays}} jours — aucun paiement avant la fin de l'essai, résiliable à tout moment. Le plan gratuit reste disponible sans carte bancaire (50 messages/mois).",
                  { days: TRIAL_DAYS.default, yearlyDays: TRIAL_DAYS.yearly }
                ),
              },
              {
                q: t("pricing.faq.q3", "Comment fonctionne la facturation ?"),
                a: t(
                  "pricing.faq.a3",
                  "La facturation est mensuelle. Vous pouvez annuler à tout moment, l'accès reste actif jusqu'à la fin de la période payée."
                ),
              },
              {
                q: t("pricing.faq.q4", "Mes données sont-elles sécurisées ?"),
                a: t(
                  "pricing.faq.a4",
                  "Absolument. Toutes les communications sont chiffrées et vos documents ne sont jamais partagés."
                ),
              },
            ].map((faq, i) => (
              <details key={i} className="glass rounded-xl group">
                <summary className="p-6 cursor-pointer flex items-center justify-between text-white font-medium hover:text-primary transition-colors">
                  {faq.q}
                  <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-6 text-slate-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-center text-slate-400 text-sm">
        <p>{t("landing.footer")}</p>
        <div className="flex justify-center gap-6 mt-3">
          <Link to="/privacy" className="hover:text-white transition-colors">
            {t("legal.nav.privacy")}
          </Link>
          <Link to="/terms" className="hover:text-white transition-colors">
            {t("legal.nav.terms")}
          </Link>
          <Link to="/legal" className="hover:text-white transition-colors">
            {t("legal.nav.legal")}
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
